// src/services/api/employeeService.ts
import {
  Employee,
  EmployeeInformationReq,
  EmployeeLeave,
  EmployeeReq,
  RecurringLeave,
  WorkHour,
} from "../../interfaces/services/Employee";
import apiUrls from "../../store/apiUrls";
import apiService, { apiCachedGet, invalidateCachePrefix } from "./apiService";

const EMP_NS = "EMP:";

const CK = {
  listBySalon: (salonCode: string) => `${EMP_NS}list:salon:${salonCode}`,
  countBySalon: (salonCode: string) => `${EMP_NS}count:salon:${salonCode}`,
  item: (code: string) => `${EMP_NS}item:${code}`,
  byUser: (userCode: string) => `${EMP_NS}byUser:${userCode}`,
};

const employeeService = {
  getEmployeeWithSalonCode: (salonCode: string): Promise<Employee[]> => {
    const url = `${apiUrls.employee.employee.getBySalon}/${salonCode}`;
    return apiCachedGet<Employee[]>("EMPLOYEE", url, {
      ttl: 5 * 60 * 1000,
      keyExtra: CK.listBySalon(salonCode),
    });
  },

  getEmployeeCountWithSalonCode: (salonCode: string): Promise<number> => {
    const url = `${apiUrls.employee.employee.getCountBySalon}/${salonCode}`;
    return apiCachedGet<number>("EMPLOYEE", url, {
      ttl: 2 * 60 * 1000,
      keyExtra: CK.countBySalon(salonCode),
    });
  },

  getEmployee: (code: string): Promise<Employee> => {
    const url = `${apiUrls.employee.employee.employee}/${code}`;
    return apiCachedGet<Employee>("EMPLOYEE", url, {
      ttl: 10 * 60 * 1000,
      keyExtra: CK.item(code),
    });
  },

  getEmployeeWithUserCode: (userCode: string): Promise<string> => {
    const url = `${apiUrls.employee.employee.byUserCode}/${userCode}`;
    return apiCachedGet<string>("EMPLOYEE", url, {
      ttl: 10 * 60 * 1000,
      keyExtra: CK.byUser(userCode),
    });
  },

  createEmployee: async (employee: EmployeeReq): Promise<string> => {
    const res = await apiService.EMPLOYEE.post(
      `${apiUrls.employee.employee.employee}`,
      employee
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  patchEmployeeInformation: async (
    code: string,
    informations: EmployeeInformationReq
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.patch(
      `${apiUrls.employee.employee.employee}/${code}`,
      informations
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  patchEmployeeWorkhours: async (
    code: string,
    workhours: WorkHour[]
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.patch(
      `${apiUrls.employee.employee.updateWorkhours}/${code}`,
      workhours
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  patchEmployeePosition: async (
    code: string,
    position: string
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.patch(
      `${apiUrls.employee.employee.updatePosition}/${code}`,
      position
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  patchEmployeeIsActive: async (
    code: string,
    isActive: boolean
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.patch(
      `${apiUrls.employee.employee.updateIsActive}/${code}`,
      isActive
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  patchEmployeeServices: async (
    code: string,
    services: string[]
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.patch(
      `${apiUrls.employee.employee.updateServices}/${code}`,
      services
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  addEmployeeLeave: async (
    code: string,
    leave: EmployeeLeave
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.post(
      `${apiUrls.employee.employee.addEmployeeLeave}/${code}`,
      leave
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  deleteEmployeeLeave: async (
    code: string,
    leaveDate: string,
    startTime: string
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.delete(
      `${apiUrls.employee.employee.deleteEmployeeLeave}/${code}/${leaveDate}/${startTime}`
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  addEmployeeRecurringLeave: async (
    code: string,
    leave: RecurringLeave
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.post(
      `${apiUrls.employee.employee.addEmployeeRecurringLeave}/${code}`,
      leave
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  deleteEmployeeRecurringLeave: async (
    code: string,
    day: string,
    startTime: string
  ): Promise<string> => {
    const res = await apiService.EMPLOYEE.delete(
      `${apiUrls.employee.employee.deleteEmployeeRecurringLeave}/${code}/${day}/${startTime}`
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },

  deleteEmployee: async (code: string): Promise<string> => {
    const res = await apiService.EMPLOYEE.delete(
      `${apiUrls.employee.employee.employee}/${code}`
    );
    invalidateCachePrefix(EMP_NS);
    return res;
  },
};

export default employeeService;
